import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import adminApi from "../../lib/adminApi";
import { ADMIN } from "../../lib/adminI18n";
import { Loader2, RefreshCw, Check, X, RotateCcw, Trash2 } from "lucide-react";

const STATUSES = ["pending_approval", "approved", "accepted", "picked_up", "on_the_way", "delivered", "cancelled"];

const STATUS_COLORS = {
  pending_approval: "bg-amber-500/15 text-amber-600",
  approved: "bg-blue-500/15 text-blue-600",
  accepted: "bg-indigo-500/15 text-indigo-600",
  picked_up: "bg-purple-500/15 text-purple-600",
  on_the_way: "bg-cyan-500/15 text-cyan-600",
  delivered: "bg-emerald-500/15 text-emerald-600",
  cancelled: "bg-red-500/15 text-red-600",
};

export default function OrdersTab() {
  const { lang } = useApp();
  const a = ADMIN[lang];
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const data = await adminApi.orders(filter);
      const list = Array.isArray(data) ? data : data?.orders || [];
      setOrders(list);
    } catch (e) {
      if (!silent) toast.error(a.error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filter, a.error]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => load(true), 20000); return () => clearInterval(id); }, [load]);

  const act = async (fn, id, okMsg) => {
    setBusy(id);
    try { await fn(); toast.success(okMsg || a.done); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || a.error); }
    finally { setBusy(""); }
  };

  const changeStatus = async (id, status) => act(() => adminApi.setStatus(id, status), id, a.done);
  const changeAmount = async (id, current) => {
    const val = window.prompt(a.setAmount, current);
    if (val == null) return;
    const amount = Number(val);
    if (Number.isNaN(amount)) return;
    act(() => adminApi.setAmount(id, amount), id);
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <div className="flex gap-1 overflow-x-auto no-scrollbar">
          {["", ...STATUSES].map((s) => (
            <button
              key={s || "all"}
              data-testid={`admin-order-filter-${s || "all"}`}
              onClick={() => setFilter(s)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}
            >
              {s === "" ? a.all : s}
            </button>
          ))}
        </div>
        <button data-testid="admin-orders-refresh" onClick={load} className="h-9 px-3 rounded-lg border border-border hover:bg-accent text-sm font-semibold flex items-center gap-1.5"><RefreshCw className="h-4 w-4" /> {a.refresh}</button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-16 text-muted-foreground"><Loader2 className="h-7 w-7 animate-spin" /></div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">{a.noOrders}</div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.order_id} data-testid={`admin-order-${o.order_id}`} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-head font-bold">{o.order_number || o.order_id}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_COLORS[o.status] || "bg-muted text-muted-foreground"}`}>{o.status}</span>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-muted text-muted-foreground">{o.payment?.method || o.payment_method} · {o.payment?.status || "—"}</span>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {a.customer}: {o.recipient_first_name} {o.recipient_last_name} · {o.phone}
                  </div>
                  <div className="text-sm text-muted-foreground">{o.dropoff_address}</div>
                  <div className="text-sm mt-1">{(o.cart_items || []).map((it, i) => (
                    <span key={i} className="inline-block me-2">{it.qty}× {it.name}{it.note ? ` (${it.note})` : ""}</span>
                  ))}</div>
                </div>
                <div className="text-end shrink-0">
                  <button onClick={() => changeAmount(o.order_id, o.amount)} className="font-bold text-lg text-primary hover:underline" data-testid={`admin-order-amount-${o.order_id}`}>{o.amount} {a.kr}</button>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <select
                  data-testid={`admin-order-status-select-${o.order_id}`}
                  value={o.status}
                  onChange={(e) => changeStatus(o.order_id, e.target.value)}
                  className="h-9 rounded-lg border border-border bg-background px-2 text-sm"
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <button data-testid={`admin-order-approve-${o.order_id}`} disabled={busy === o.order_id} onClick={() => act(() => adminApi.approve(o.order_id), o.order_id)} className="h-9 px-3 rounded-lg bg-emerald-500/15 text-emerald-600 text-sm font-semibold flex items-center gap-1 disabled:opacity-50"><Check className="h-4 w-4" /> {a.approve}</button>
                <button data-testid={`admin-order-reject-${o.order_id}`} disabled={busy === o.order_id} onClick={() => act(() => adminApi.reject(o.order_id), o.order_id)} className="h-9 px-3 rounded-lg bg-amber-500/15 text-amber-600 text-sm font-semibold flex items-center gap-1 disabled:opacity-50"><X className="h-4 w-4" /> {a.reject}</button>
                {(o.payment?.method === "card" || o.payment_method === "card") && (
                  <button disabled={busy === o.order_id} onClick={() => act(() => adminApi.refund(o.order_id), o.order_id)} className="h-9 px-3 rounded-lg bg-blue-500/15 text-blue-600 text-sm font-semibold flex items-center gap-1 disabled:opacity-50"><RotateCcw className="h-4 w-4" /> {a.refund}</button>
                )}
                <button data-testid={`admin-order-delete-${o.order_id}`} disabled={busy === o.order_id} onClick={() => window.confirm(a.confirmDelete) && act(() => adminApi.deleteOrder(o.order_id), o.order_id)} className="h-9 px-3 rounded-lg bg-red-500/15 text-red-600 text-sm font-semibold flex items-center gap-1 disabled:opacity-50 ms-auto"><Trash2 className="h-4 w-4" /> {a.delete}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
